import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { type Theme } from '@mui/material';
import { type SystemStyleObject } from '@mui/system';
import { type VideoType } from '@tmdb-graphql-api/schema/types';
import { useEffect } from 'react';
import { Loader } from '#components/Loader/index.tsx';
import { TrailerPlayer } from '#components/TrailerPlayer/index.tsx';
import { getVideos } from '#components/VideoButton/helpers/getVideos.ts';
import { type MovieData, type SeasonData, type TvData } from '#components/VideoButton/types.ts';
import { ScreenFormatType } from '#enums/mediaTypes.ts';
import { buildVideoPaths } from '#helpers/buildVideoPaths.ts';

const queries = {
  [ScreenFormatType.Episode]: "\nquery GetEpisodeVideos(\n  $id: ID!\n) {\n  episode(id: $id) {\n    ...EpisodeVideos\n  }\n}\n\nfragment EpisodeVideos on Episode {\n  videos {\n    name\n    key\n    site\n    type\n  }\n}\n",
  [ScreenFormatType.Movie]: "\nquery GetMovieVideos(\n  $id: ID!\n) {\n  movie(id: $id) {\n    ...MovieVideos\n  }\n}\n\nfragment MovieVideos on Movie {\n  videos {\n    name\n    key\n    site\n    type\n  }\n}\n",
  [ScreenFormatType.Season]: "\nquery GetSeasonVideos(\n  $id: ID!\n) {\n  season(id: $id) {\n    ...SeasonVideos\n  }\n}\n\nfragment SeasonVideos on Season {\n  videos {\n    name\n    key\n    site\n    type\n  }\n}\n",
  [ScreenFormatType.Tv]: "\nquery GetTvVideos(\n  $id: ID!\n) {\n  tv(id: $id) {\n    ...TvVideos\n  }\n}\n\nfragment TvVideos on Tv {\n  videos {\n    name\n    key\n    site\n    type\n  }\n}\n",
};

export type TrailerPlayerTabProps = {
  id: string;
  sx?: SystemStyleObject<Theme>;
  type: ScreenFormatType;
  videoCount?: number;
  videoTypes?: VideoType[];
};

export const TrailerPlayerTabPanel = ({ id, type, videoCount, videoTypes }: TrailerPlayerTabProps) => {
  const query = queries[type];

  const { execute, loading, result } = useQuery<MovieData | SeasonData | TvData>(query, {
    loading: true,
  });

  useEffect(() => {
    void execute({ variables: { id } }, { data: { initiator: 'TrailerPlayerTab' } });
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return <Loader sx={{ bottom: 0, left: 0, position: 'absolute', right: 0, top: 0 }} />;
  }

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  const videos = getVideos(result?.data);

  if (videos.length === 0) {
    return;
  }

  return (
    <TrailerPlayer
      config={{
        youtube: {
          playerVars: {
            autoplay: false,
          },
        },
      }}
      height="40%"
      responsiveHeights={{
        md: `40%`,
        sm: '56.25%',
        xs: '75%',
      }}
      url={buildVideoPaths(videos, { count: videoCount, types: videoTypes })}
    />
  );
};
